import React, { useState } from 'react';
import Icon from 'components/AppIcon';
import { AVAILABLE_REPORTS } from './esgData';

const REPORT_TYPES = ['Q3 2025 ESG Report', 'Annual Sustainability Report', 'TCFD Disclosure', 'Impact Report', 'GHG Inventory', 'Custom Report'];
const PERIODS_OPT = ['Q1 2026', 'Q4 2025', 'Q3 2025', 'Q2 2025', 'Q1 2025', 'Full Year 2024'];
const ASSETS_OPT = ['All Assets', 'Mersey Tidal Array', 'Pennine Wind Farm', 'Anglian Water Treatment', 'Bristol Solar Park', 'Northern Rail Freight Hub', 'Thames Digital Corridor'];

function PreviewModal({ report, onClose }) {
  return (
    <div className="fixed inset-0 flex items-center justify-center z-50" style={{ background: 'rgba(0,0,0,0.5)' }}>
      <div className="bg-card rounded-2xl shadow-2xl border border-border w-full max-w-2xl mx-4 overflow-hidden">
        {/* Modal header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-border" style={{ background: '#0f4c81' }}>
          <div className="flex items-center gap-3">
            <Icon name="FileText" size={20} color="#fff" />
            <div>
              <p className="font-heading font-semibold text-white" style={{ fontSize: '0.9375rem' }}>{report?.name}</p>
              <p className="font-caption text-[rgba(224,224,224,0.65)] text-xs">SeedDrive Infrastructure Equity — Preview</p>
            </div>
          </div>
          <button onClick={onClose} className="text-white hover:text-[rgba(224,224,224,0.65)] transition-colors">
            <Icon name="X" size={20} color="currentColor" />
          </button>
        </div>
        {/* Preview body */}
        <div className="p-6 space-y-4 overflow-y-auto" style={{ maxHeight: '70vh' }}>
          <div className="text-center py-4 border-b border-border">
            <p className="font-heading font-bold text-foreground" style={{ fontSize: '1.25rem' }}>{report?.name}</p>
            <p className="font-caption text-muted-foreground text-sm mt-1">SeedDrive Infrastructure Equity Platform</p>
            <p className="font-caption text-muted-foreground text-xs mt-0.5">Generated: {report?.generated} · Sample data — for demonstration purposes only</p>
          </div>
          <div>
            <p className="font-heading font-semibold text-foreground mb-2" style={{ fontSize: '0.875rem' }}>Table of Contents</p>
            {['1. Executive Summary', '2. Portfolio ESG Score Overview', '3. Environmental Performance', '4. Social Impact Metrics', '5. Governance & Compliance', '6. Asset-Level Analysis', '7. Methodology & Data Sources', '8. Appendices']?.map((item) => (
              <div key={item} className="flex items-center justify-between py-1.5 border-b border-border/50">
                <span className="font-caption text-foreground text-sm">{item}</span>
                <span className="font-caption text-muted-foreground text-xs">p. {Math.floor(Math.random() * 20) + 2}</span>
              </div>
            ))}
          </div>
          <div className="rounded-lg border border-border p-4 bg-muted/30">
            <p className="font-heading font-semibold text-foreground mb-3" style={{ fontSize: '0.875rem' }}>Sample Data Table — Portfolio ESG Scores</p>
            <table className="w-full" style={{ fontSize: '0.8rem' }}>
              <thead>
                <tr className="border-b border-border">
                  {['Asset', 'E', 'S', 'G', 'Overall']?.map((h) => (
                    <th key={h} className="text-left pb-1.5 pr-3 font-caption text-muted-foreground font-medium" style={{ fontSize: '0.72rem' }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {[['Mersey Tidal Array', 68, 72, 65, 68], ['Pennine Wind Farm', 82, 74, 76, 77], ['Anglian Water Treatment', 74, 80, 78, 77]]?.map(([name, e, s, g, o]) => (
                  <tr key={name}>
                    <td className="py-1.5 pr-3 font-caption text-foreground">{name}</td>
                    <td className="py-1.5 pr-3 font-caption text-foreground">{e}</td>
                    <td className="py-1.5 pr-3 font-caption text-foreground">{s}</td>
                    <td className="py-1.5 pr-3 font-caption text-foreground">{g}</td>
                    <td className="py-1.5 font-caption font-semibold text-foreground">{o}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <p className="font-caption text-muted-foreground text-xs text-center">This is a preview placeholder. Full report available upon download.</p>
        </div>
      </div>
    </div>
  );
}

function GenerateModal({ onClose, onGenerate }) {
  const [reportType, setReportType] = useState(REPORT_TYPES?.[0]);
  const [period, setPeriod] = useState(PERIODS_OPT?.[2]);
  const [asset, setAsset] = useState(ASSETS_OPT?.[0]);
  const [format, setFormat] = useState('PDF');

  return (
    <div className="fixed inset-0 flex items-center justify-center z-50" style={{ background: 'rgba(0,0,0,0.5)' }}>
      <div className="bg-card rounded-2xl shadow-2xl border border-border w-full max-w-md mx-4">
        <div className="flex items-center justify-between px-6 py-4 border-b border-border">
          <h3 className="font-heading font-semibold text-foreground" style={{ fontSize: '1rem' }}>Generate New Report</h3>
          <button onClick={onClose}><Icon name="X" size={18} color="var(--color-muted-foreground)" /></button>
        </div>
        <div className="p-6 space-y-4">
          <div>
            <label className="font-caption text-sm font-medium text-foreground block mb-1.5">Report Type</label>
            <select value={reportType} onChange={(e) => setReportType(e?.target?.value)} className="w-full border border-border rounded-lg px-3 py-2 font-caption text-sm bg-card text-foreground focus:outline-none focus:ring-2 focus:ring-primary">
              {REPORT_TYPES?.map((r) => <option key={r}>{r}</option>)}
            </select>
          </div>
          <div>
            <label className="font-caption text-sm font-medium text-foreground block mb-1.5">Reporting Period</label>
            <select value={period} onChange={(e) => setPeriod(e?.target?.value)} className="w-full border border-border rounded-lg px-3 py-2 font-caption text-sm bg-card text-foreground focus:outline-none focus:ring-2 focus:ring-primary">
              {PERIODS_OPT?.map((p) => <option key={p}>{p}</option>)}
            </select>
          </div>
          <div>
            <label className="font-caption text-sm font-medium text-foreground block mb-1.5">Asset Filter</label>
            <select value={asset} onChange={(e) => setAsset(e?.target?.value)} className="w-full border border-border rounded-lg px-3 py-2 font-caption text-sm bg-card text-foreground focus:outline-none focus:ring-2 focus:ring-primary">
              {ASSETS_OPT?.map((a) => <option key={a}>{a}</option>)}
            </select>
          </div>
          <div>
            <label className="font-caption text-sm font-medium text-foreground block mb-1.5">Format</label>
            <div className="flex gap-3">
              {['PDF', 'XLSX']?.map((f) => (
                <button
                  key={f}
                  onClick={() => setFormat(f)}
                  className="flex-1 py-2 rounded-lg border font-caption text-sm font-medium transition-all"
                  style={{
                    borderColor: format === f ? 'var(--color-primary)' : 'var(--color-border)',
                    background: format === f ? 'var(--color-primary)' : 'transparent',
                    color: format === f ? '#fff' : 'var(--color-foreground)',
                  }}
                >
                  {f}
                </button>
              ))}
            </div>
          </div>
          <button
            onClick={() => onGenerate(reportType, period, format)}
            className="w-full py-2.5 rounded-lg font-caption text-sm font-semibold text-white transition-all hover:opacity-90"
            style={{ background: 'var(--color-primary)' }}
          >
            Generate Report
          </button>
        </div>
      </div>
    </div>
  );
}

export default function ReportingPanel({ onToast }) {
  const [previewReport, setPreviewReport] = useState(null);
  const [generateOpen, setGenerateOpen] = useState(false);

  const handleDownload = (report) => {
    onToast?.(`Downloading ${report?.name}…`);
  };

  const handleGenerate = (type, period, format) => {
    setGenerateOpen(false);
    onToast?.('Report generation queued — you will be notified when ready.');
  };

  return (
    <>
      {previewReport && <PreviewModal report={previewReport} onClose={() => setPreviewReport(null)} />}
      {generateOpen && <GenerateModal onClose={() => setGenerateOpen(false)} onGenerate={handleGenerate} />}
      <div className="card" style={{ padding: '20px 24px' }}>
        <div className="flex items-center justify-between mb-4">
          <div>
            <h3 className="font-heading font-semibold text-foreground" style={{ fontSize: '0.9375rem' }}>Available Reports</h3>
            <p className="font-caption text-muted-foreground text-xs mt-0.5">Download or preview generated reports</p>
          </div>
          <button
            onClick={() => setGenerateOpen(true)}
            className="flex items-center gap-2 px-4 py-2 rounded-lg font-caption text-sm font-semibold text-white transition-all hover:opacity-90"
            style={{ background: 'var(--color-primary)' }}
          >
            <Icon name="Plus" size={15} color="#fff" />
            Generate New Report
          </button>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-4">
          {AVAILABLE_REPORTS?.map((report) => (
            <div key={report?.id} className="rounded-xl border border-border bg-background p-4 flex flex-col gap-3 hover:shadow-md transition-shadow">
              <div className="flex items-start justify-between">
                <div className="w-10 h-10 rounded-lg flex items-center justify-center" style={{ background: report?.type === 'PDF' ? '#fee2e2' : '#dcfce7' }}>
                  <Icon name={report?.icon} size={20} color={report?.type === 'PDF' ? '#dc2626' : '#16a34a'} />
                </div>
                <span className="inline-flex items-center px-2 py-0.5 rounded font-caption text-xs font-semibold" style={{ background: report?.type === 'PDF' ? '#fee2e2' : '#dcfce7', color: report?.type === 'PDF' ? '#dc2626' : '#16a34a' }}>
                  {report?.type}
                </span>
              </div>
              <div>
                <p className="font-caption font-semibold text-foreground" style={{ fontSize: '0.8125rem', lineHeight: 1.4 }}>{report?.name}</p>
                <p className="font-caption text-muted-foreground text-xs mt-0.5">Generated {report?.generated}</p>
              </div>
              <div className="flex gap-2 mt-auto">
                <button
                  onClick={() => handleDownload(report)}
                  className="flex-1 flex items-center justify-center gap-1.5 py-1.5 rounded-lg border border-border font-caption text-xs font-medium text-foreground hover:bg-muted transition-colors"
                >
                  <Icon name="Download" size={12} color="var(--color-foreground)" />
                  Download
                </button>
                <button
                  onClick={() => setPreviewReport(report)}
                  className="flex-1 flex items-center justify-center gap-1.5 py-1.5 rounded-lg font-caption text-xs font-medium text-white transition-all hover:opacity-90"
                  style={{ background: 'var(--color-primary)' }}
                >
                  <Icon name="Eye" size={12} color="#fff" />
                  Preview
                </button>
              </div>
            </div>
          ))}
        </div>
      </div>
    </>
  );
}
